import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { validateCpf, formatCpfNumber } from '@/app/utils/cpfFormater'
import { useQuery } from '@tanstack/react-query'
import { crefisaService } from '@/app/services/crefisaService'
import { useCrefisaContext } from '@/app/contexts/CrefisaContext'
import { useEffect, useMemo, useState } from 'react'
import { useToast } from '@/view/components/ui/use-toast'
import { useCustomerCpf } from '@/app/hooks/useCustomerCpf'
import { useCrefisaAction } from '@/app/hooks/useCrefisaStream'
import masks from '@/app/utils/masks'
import { PartnerType } from '@/app/utils/partners.enum'
import { getAgeFromBirthDate } from '@/app/utils/getAgeFromBirthDate'
import { useRegisterSupportContext } from '@/app/store/supportScreenContext'
import { journeyStore } from "@/app/services/journey"

const OVERDUE_CONTRACT_MESSAGE =
  "Cliente com contrato em atraso. Não é possível atender solicitação de empréstimo";

const currencyToNumber = (value: unknown): number | "" => {
  const digits = String(value ?? "").replace(/\D/g, "");
  return digits ? Number(digits) / 100 : "";
};

const buildInssSimulationData = (values: GenerateProposalFormData) => {
  const hasLoan = values.possuiEmprestimo === "sim";
  const hasDebits = values.possuiDebitos === "sim";

  const recebeViaCrefisa =
    values.recebeViaCrefisa === "sim"
      ? 60
      : values.recebeViaCrefisa === "nao"
        ? 30
        : "";

  return {
    valorBeneficio: currencyToNumber(values.valorBeneficio),
    possuiEmprestimo: values.possuiEmprestimo ? hasLoan : "",
    valorEmprestimoAtivo: hasLoan
      ? currencyToNumber(values.valorEmprestimoAtivo)
      : "",
    recebeViaCrefisa,
    naoRecebeViaCrefisa: values.recebeViaCrefisa === "nao",
    possuiDebitos: values.possuiDebitos ? hasDebits : "",
    valorTotalDebitos: hasDebits
      ? currencyToNumber(values.valorTotalDebitos)
      : "",
  } as const;
};

const buildGenerateProposalSchema = (isInss: boolean) =>
  z.object({
    cpf: z
      .string()
      .min(11, "Campo obrigatório")
      .refine((v) => validateCpf(v), "CPF Inválido")
      .transform((f) => f.replace(/\D/g, "")),
    phone: z
      .string({
        required_error: "Campo obrigatório",
      })
      .min(10, "Telefone inválido")
      .transform((v) => v.replace(/\D/g, "")),
    birthDate: isInss
      ? z
          .string()
          .min(10, "Data inválida")
          .refine((value) => {
            const age = getAgeFromBirthDate(value);
            return age === null || age >= 18;
          }, "Deve ser maior de idade")
      : z.string().optional(),
    codeReceiptDataId: z.string().min(1, "Campo obrigatório"),
    valorBeneficio: isInss
      ? z.string().min(1, "Campo obrigatório")
      : z.string().optional(),
    possuiEmprestimo: isInss
      ? z.string().min(1, "Campo obrigatório")
      : z.string().optional(),
    valorEmprestimoAtivo: z.string().optional(),
    recebeViaCrefisa: isInss
      ? z.string().min(1, "Campo obrigatório")
      : z.string().optional(),
    possuiDebitos: isInss
      ? z.string().min(1, "Campo obrigatório")
      : z.string().optional(),
    valorTotalDebitos: z.string().optional(),
  });

type GenerateProposalFormData = {
  cpf: string;
  phone: string;
  birthDate?: string;
  codeReceiptDataId?: string;
  valorBeneficio?: string;
  possuiEmprestimo?: string;
  valorEmprestimoAtivo?: string;
  recebeViaCrefisa?: string;
  possuiDebitos?: string;
  valorTotalDebitos?: string;
};

interface UseGenerateProposalModalProps {
  partnerType?: PartnerType.CREFISA | PartnerType.CREFISA_INSS;
  onContinue?: () => void;
  onContinuePreApproved?: () => void;
}

export default function useGenerateProposalModal({
  partnerType = PartnerType.CREFISA,
  onContinue,
  onContinuePreApproved,
}: UseGenerateProposalModalProps) {
  const { toast } = useToast();
  const {
    setSimulation,
    setCustomer,
    setProposal,
    customerData,
    setConsentBankAccount,
  } = useCrefisaContext();

  const isInss = partnerType === PartnerType.CREFISA_INSS;

  const [isLoading, setIsLoading] = useState(false);

  const schema = useMemo(() => buildGenerateProposalSchema(isInss), [isInss]);

  const getGenerateProposalForm = useForm<GenerateProposalFormData>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      cpf: customerData?.cpf ? formatCpfNumber(customerData.cpf) : "",
      phone: "",
      birthDate: "",
      codeReceiptDataId: "",
      valorBeneficio: "",
      possuiEmprestimo: "",
      valorEmprestimoAtivo: "",
      recebeViaCrefisa: "",
      possuiDebitos: "",
      valorTotalDebitos: "",
    },
  });

  const { handleSubmit, watch, setValue, getValues } = getGenerateProposalForm;

  const cpfValue = watch("cpf");
  const cleanCpf = (cpfValue ?? "").replace(/\D/g, "");

  const birthDateValue = watch("birthDate");
  const birthDateAge = getAgeFromBirthDate(birthDateValue);
  const isUnderage = isInss && birthDateAge !== null && birthDateAge < 18;

  const { data: receivedDates = [] } = useQuery({
    queryKey: ["crefisa-received-dates", partnerType],
    queryFn: () => crefisaService.getReceivedDates(partnerType),
  });

  const receivedDateOptions = useMemo(
    () =>
      receivedDates.map((item) => ({
        label: item.descricao,
        value: String(item.codigoDataRecebimentoId),
      })),
    [receivedDates],
  );

  const { data: existingCustomer } = useCustomerCpf(cleanCpf);

  useEffect(() => {
    if (existingCustomer?.phone && !getValues("phone")) {
      setValue("phone", masks.phone(existingCustomer.phone), {
        shouldValidate: true,
      });
    }
  }, [existingCustomer, getValues, setValue]);

  const {
    run: getOperations,
    steps,
    isRunning: isLoadingOperations,
  } = useCrefisaAction((cpf: string, context) =>
    crefisaService.getOperations(cpf, context),
  );

  const submitModal = handleSubmit(async (data) => {
    if (isInss) {
      const age = getAgeFromBirthDate(data.birthDate);
      if (age !== null && age < 18) {
        toast({
          title: "Cliente menor de idade",
          description:
            "Não é possível prosseguir para clientes menores de 18 anos.",
          variant: "destructive",
        });
        return;
      }
    }

    try {
      setIsLoading(true);
      journeyStore.start(partnerType);

      setConsentBankAccount(null);

      const operations = await getOperations(data.cpf);

      if (!operations.clienteId?.trim()) {
        const message =
          operations.mensagem === OVERDUE_CONTRACT_MESSAGE
            ? "Cliente sem Perfil"
            : operations.mensagem;

        toast({
          title: "Erro ao gerar proposta",
          description: message || "Não foi possível buscar simulações",
          variant: "destructive",
        });

        return;
      }

      setCustomer({
        cpf: data.cpf,
        phone: data.phone,
        ...(isInss && data.birthDate ? { birthDate: data.birthDate } : {}),
        income: {
          ...customerData?.income,
          registration: customerData?.income?.registration ?? "",
          registrationDigit: customerData?.income?.registrationDigit ?? "",
          valueIncome: customerData?.income?.valueIncome ?? 0,
          codeReceiptDataId: Number(data.codeReceiptDataId),
        },
      });

      setSimulation({
        clientId: operations.clienteId,
        isRefinancing: operations.permiteCapturaRefin,
        borrowedValue: 0,
        preApproveOffers: operations.ofertasPreAprovadas ?? [],
        type: partnerType,
        ...(isInss
          ? buildInssSimulationData(data)
          : { naoRecebeViaCrefisa: true }),
      });

      const firstOffer = operations.ofertasPreAprovadas?.[0];

      if (firstOffer) {
        setProposal({
          offerId: firstOffer.idOferta,
          value: firstOffer.valorSimulacao,
          prazo: firstOffer.quantidadeParcelas,
        });

        onContinuePreApproved?.();
        return;
      }

      onContinue?.();
    } catch (error: any) {
      const message =
        error?.message ??
        error?.Message ??
        error?.mensagem ??
        "Não foi possível buscar simulações";

      toast({
        title: "Erro ao gerar proposta",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  });

  useRegisterSupportContext('simulation.crefisa.generate', {
    data: getGenerateProposalForm.watch(),
  })

  return {
    form: getGenerateProposalForm,
    submitModal,
    loading: isLoadingOperations || isLoading,
    steps,
    isInss,
    isUnderage,
    receivedDateOptions,
  };
}
